import React, { useState, useEffect } from 'react';

import axios from 'axios';
import { Spinner, Row, Col } from 'react-bootstrap';
import { RouteComponentProps } from 'react-router-dom';
// @ts-ignore
import createFilterOptions from 'react-select-fast-filter-options';

import queryString from 'query-string';
import 'react-virtualized/styles.css';
import 'react-virtualized-select/styles.css';
import 'react-datepicker/dist/react-datepicker.css';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import { IAuthContext } from '../context/auth/auth-context';
import withAuthContext from '../context/auth/AuthConsumer';
import { CeresLogger } from '../logger';
import './react-select.css';
import Emission from '../components/benchmarking/Emission';
import NoRecordImage from '../assets/no-record-img.png';
import { CorporateSelect, ResultContainer, HeaderRow } from '../components/custom-styled-components/CommonStyledComponents';
import { emissionType, emissionTypeConstants } from '../util/benchmarking.util';
import { API_BASE_URL, API_AUTH_HEADERS } from '../util/api.util';
import ReactGA from 'react-ga4';
import { Card, Heading, Kicker, Subheading } from '../components/ui';

const PageRoot = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ContentInner = styled.div`
  margin: 4em 5em;
  box-sizing: border-box;
`;


const GlassControlsCard = styled(Card)<{ $expanded?: boolean }>`
  padding: 1.35rem 1.25rem 1.5rem;
  margin-bottom: ${(p) => (p.$expanded ? '12rem' : '1.25rem')};
  transition: margin-bottom 0.2s ease;
`;

const ControlsTitle = styled.h3`
  margin: 0 0 1rem;
  font-size: 1rem;
  font-weight: 600;
  color: #f8fafc;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', sans-serif;
`;

const GlassResults = styled(Card)`
  padding: 0.5rem;
`;

const CenteredContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 40vh;
  flex-direction: column;
  gap: 12px;
`;

const NoRecordWrap = styled(Card)`
  text-align: center;
  padding: 1.5rem;
  border: 1px solid rgba(148, 163, 184, 0.15);
  max-width: 420px;
  margin: 0 auto;

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
  }
`;

const NoRecordCaption = styled.p`
  margin: 1rem 0 0;
  color: #94a3b8;
  font-size: 15px;
`;

const Benchmarking: React.FC<
  RouteComponentProps<any> & {
    context: IAuthContext;
  }
> = (props) => {
  const [entity, setEntity] = useState<any>();
  const [entityType, setEntityType] = useState<any>();
  const [filter, setFilter] = useState();
  const [statsResult, setStatsResult] = useState<any>();
  const [statsCnResults, setStatsCnResults] = useState<any>();
  const [isDisabled, setIsDisabled] = useState(true);
  const [resultRecords, setResultRecords] = useState<any>();
  const [noRecords, setNoRecords] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cnNumber, setCnNumber] = useState('');
  const [entityMenuOpen, setEntityMenuOpen] = useState(false);
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);

  useEffect(() => {
    getCorporates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (window.top !== window.self && resultRecords) {
      const filterData = queryString.parse(props.history.location.search);
      if (filterData.rn) {
        const entityFilterRecord = searchParam(resultRecords, filterData.rn) || filterData.rn;
        const entityFilterType = emissionType[0];

        setEntity(entityFilterRecord);
        setEntityType(entityFilterType);

        if (typeof entityFilterRecord === 'object') {
          fetchRNStats(entityFilterType.value, entityFilterRecord.value);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultRecords]);

  const fetchRNStats = async (type: string, value: any) => {
    setIsDisabled(true);
    setLoading(true);
    ReactGA.event({
      category: 'Search',
      action: 'Benchmarking RN Search',
      label: value,
    });
    const apiEndpoints: Record<string, string> = {
      [emissionTypeConstants.EMISSIONS_EVENTS]: `${API_BASE_URL}/api/fetchRNStats`,
      [emissionTypeConstants.ANNUAL_FACILITY_EMISSIONS]: `${API_BASE_URL}/api/fetchAirFacilityEmissionsNStats`,
      [emissionTypeConstants.ENFORCEMENT]: `${API_BASE_URL}/BenchmarkingEnforcement?action=rn_details&rn=${value}`,
    };

    const emissionApiUrl = apiEndpoints[type];
    if (!emissionApiUrl) {
      setIsDisabled(false);
      setLoading(false);
      return;
    }

    try {
      if (type !== emissionTypeConstants.ENFORCEMENT) {
        const response = await axios.post(emissionApiUrl, { RN: value }, {
          headers: API_AUTH_HEADERS,
        });
        setStatsResult(response.data[0] || '');
        setNoRecords(!response.data.length);
      } else {
        const response = await axios.get(emissionApiUrl);
        if (response.data.data.length) {
          const cNumber = response.data.data[0]['CN Number'];
          setCnNumber(cNumber);
          setStatsResult(response.data.data[0].enforcement_list);
          setNoRecords(false);

          const cnResponse = await axios.get(
            API_BASE_URL + `/BenchmarkingEnforcement?action=cn_details&rn=${value}&cn=${cNumber}`
          );

          if (cnResponse.data.data.length) {
            const enforcementData = cnResponse.data.data;
            const data = enforcementData.filter((item: any) => !!item['CN Number']);
            setStatsCnResults(data);
          }
        } else {
          setNoRecords(true);
        }
      }
    } catch (error: any) {
      setNoRecords(true);
      setStatsResult('');
      setStatsCnResults('');
      toast.error(<span>Error! No record found for <b>{value}</b></span>);
      CeresLogger.error({
        message: error instanceof Error ? error.message : 'Benchmarking fetch error',
        payload: { error },
      });
    } finally {
      setIsDisabled(false);
      setLoading(false);
    }
  };

  const getCorporates = async () => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/eventEmissions/corporates`,
        {
          headers: API_AUTH_HEADERS,
        }
      );

      const records = response.data[0].jsonlistb || [];
      setResultRecords(records);
      setFilter(() => createFilterOptions({ options: records }));

      const filterData: any = queryString.parse(props.history.location.search);
      if (filterData.rn && filterData.event) {
        const entityFilterRecord = searchParam(records, filterData.rn);
        const entityFilterType = searchParam(emissionType, parseInt(filterData.event, 10));
        setEntity(entityFilterRecord);
        setEntityType(entityFilterType);
        await fetchRNStats(entityFilterType.value, entityFilterRecord.value);
      }
    } catch (error: any) {
      CeresLogger.error({
        message: error instanceof Error ? error.message : 'Benchmarking corporates error',
        payload: { error },
      });
    } finally {
      setIsDisabled(false);
    }
  };

  const searchParam = (data: any[], value: any): any | undefined => {
    return data.find((aData) => aData.value === value);
  };

  return (
    <PageRoot className="benchmarking-page">
      <ContentInner>
        <Kicker align="center">Benchmarking</Kicker>
        <Heading level={1} size="xl" align="center" accentText="benchmarking" italicAccent>
          Facility
        </Heading>
        <Subheading align="center" style={{ margin: '0 0 1.5rem' }}>
          TRI emissions, events, and enforcement by registered entity
        </Subheading>

        <GlassControlsCard $expanded={entityMenuOpen || typeMenuOpen}>
          <ControlsTitle>Select entity and data type</ControlsTitle>
          <HeaderRow>
            {!!isDisabled && (
              <div>
                <Spinner animation="border" size="sm" style={{ color: '#34d399', width: '1.25rem', height: '1.25rem' }} />
              </div>
            )}
          </HeaderRow>
          <ResultContainer style={{ padding: 0 }}>
            <Row className="g-3">
              <Col md={8}>
                <CorporateSelect
                  value={entity}
                  className="basic-single"
                  filterOptions={filter}
                  disabled={isDisabled}
                  name="corporate1"
                  options={resultRecords}
                  onFocus={() => setEntityMenuOpen(true)}
                  onBlur={() => setEntityMenuOpen(false)}
                  onChange={(e: any) => {
                    setEntityMenuOpen(false);
                    setEntity(e);
                    if (e && entityType) {
                      fetchRNStats(entityType.value, e.value);
                    } else {
                      return null;
                    }
                  }}
                />
              </Col>
              <Col md={4}>
                <CorporateSelect
                  value={entityType}
                  className="basic-single"
                  filterOptions={filter}
                  disabled={isDisabled}
                  name="events"
                  options={emissionType}
                  onFocus={() => setTypeMenuOpen(true)}
                  onBlur={() => setTypeMenuOpen(false)}
                  onChange={(e: any) => {
                    setTypeMenuOpen(false);
                    setEntityType(e);
                    if (!e) {
                      return;
                    }
                    if (!entity) {
                      toast.info('Please select an Entity!');
                      return;
                    }
                    fetchRNStats(e.value, entity.value);
                  }}
                />
              </Col>
            </Row>
          </ResultContainer>
        </GlassControlsCard>

        {loading && (
          <CenteredContainer>
            <Spinner animation="border" style={{ color: '#34d399', width: '2.5rem', height: '2.5rem' }} />
          </CenteredContainer>
        )}

        {!loading && noRecords && (
          <CenteredContainer>
            <NoRecordWrap>
              <img src={NoRecordImage} alt="No records found" />
              <NoRecordCaption>No benchmarking records match this selection.</NoRecordCaption>
            </NoRecordWrap>
          </CenteredContainer>
        )}

        {!loading && statsResult && entity && entityType && (
          <GlassResults>
            <ResultContainer>
              <Emission result={statsResult} cnNumber={cnNumber} entity={entity} types={entityType} cnStatsResult={statsCnResults} />
            </ResultContainer>
          </GlassResults>
        )}
      </ContentInner>
    </PageRoot>
  );
};

export default withAuthContext(Benchmarking);
